import React from "react";
import {
  Box,
  Input,
  Flex,
  Text,
  Badge,
  VStack,
  SimpleGrid,
} from "@chakra-ui/react";
import CustomButton from "../../../CustomUi/CustomButton";
import CustomPopover from "../../../CustomUi/CustomPopover";
import SelectInput from "../../../CustomUi/SelectInput";
import TextInput from "../../../CustomUi/TextInput";
import useThemeColors from "../../../../hooks/useThemeColors";
import useIcons from "../../../../hooks/useIcons";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";

interface AuditTrailFiltersProps {
  // Filter states
  userIdFilter: string;
  setUserIdFilter: (value: string) => void;
  actionFilter: string;
  setActionFilter: (value: string) => void;
  entityTypeFilter: string;
  setEntityTypeFilter: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;

  // Actions
  onFilter: () => void;
  onClearFilters: () => void;
  loading: boolean;
  actionOptions: { label: string; value: string }[];
  entityTypeOptions: { label: string; value: string }[];
  userOptions: { label: string; value: string }[];
}

const AuditTrailFilters: React.FC<AuditTrailFiltersProps> = ({
  userIdFilter,
  setUserIdFilter,
  actionFilter,
  setActionFilter,
  entityTypeFilter,
  setEntityTypeFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  onFilter,
  onClearFilters,
  loading,
  actionOptions,
  entityTypeOptions,
  userOptions,
}) => {
  const colors = useThemeColors();
  const icons = useIcons();
  const { users } = useSchoolsContext();

  const hasActiveFilters =
    userIdFilter ||
    actionFilter ||
    (entityTypeFilter && entityTypeFilter !== "All entities") ||
    startDate ||
    endDate ||
    searchTerm;

  const getFilterSummary = () => {
    const filters = [];
    if (userIdFilter) {
      const user = users.find((u) => u.id === userIdFilter);
      filters.push(
        `User: ${user ? `${user.firstName} ${user.lastName}` : userIdFilter}`
      );
    }
    if (actionFilter) filters.push(`Action: ${actionFilter}`);
    if (entityTypeFilter && entityTypeFilter !== "All entities")
      filters.push(`Entity: ${entityTypeFilter}`);
    if (startDate)
      filters.push(`From: ${new Date(startDate).toLocaleDateString()}`);
    if (endDate) filters.push(`To: ${new Date(endDate).toLocaleDateString()}`);
    if (searchTerm) filters.push(`Search: "${searchTerm}"`);
    return filters;
  };

  return (
    <Box w={"full"}>
      <Flex
        gap={3}
        align="center"
        wrap="wrap"
        flexDir={["column", "row"]}
        alignItems={["stretch", "center"]}
      >
        {/* Search Input - Always visible */}
        <Box flex="1" minW={["auto", "250px"]}>
          <TextInput
            placeholder="Search audit trails current page..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            name={"searchTerm"}
            startElement={<icons.utility.search />}
          />
        </Box>

        {/* Filters Popover */}
        <CustomPopover
          w={"450px"}
          maxW="450px"
          header={<Text fontWeight="semibold">Filters</Text>}
          trigger={
            <CustomButton
              icon={icons.utility.filter}
              label="Filters"
              variant="outline"
              size="sm"
            />
          }
        >
          <VStack gap={4} align="stretch">
            {/* Action and Entity Type Filters - Responsive Layout */}
            <SimpleGrid columns={[1, 2]} gap={3}>
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Action
                </Text>
                <SelectInput
                  label=""
                  name="action"
                  value={actionFilter ? actionFilter : ""}
                  onChange={(e) => setActionFilter(e.target.value)}
                  collection={actionOptions}
                  placeholder="All actions"
                />
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Entity Type
                </Text>
                <SelectInput
                  label=""
                  name="entityType"
                  value={entityTypeFilter ? entityTypeFilter : ""}
                  onChange={(e) => setEntityTypeFilter(e.target.value)}
                  collection={entityTypeOptions}
                  placeholder="All Types"
                />
              </Box>
            </SimpleGrid>

            {/* User Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                User
              </Text>
              <SelectInput
                label=""
                name="user"
                value={userIdFilter ? userIdFilter : ""}
                onChange={(e) => setUserIdFilter(e.target.value)}
                collection={userOptions}
                placeholder="All users"
              />
            </Box>

            {/* Date Range - Responsive Layout */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Date Range
              </Text>
              <SimpleGrid columns={[1, 2]} gap={2}>
                <Input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="sm"
                  placeholder="Start"
                  fontSize="xs"
                />
                <Input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="sm"
                  placeholder="End"
                  fontSize="xs"
                />
              </SimpleGrid>
            </Box>

            {/* Filter Actions */}
            <SimpleGrid columns={[1, 2]} gap={2} pt={2}>
              <CustomButton
                label="Apply"
                colorPalette={colors.schoolAccent.primary}
                onClick={onFilter}
                loading={loading}
                size="sm"
              />
              <CustomButton
                label="Clear"
                variant="outline"
                onClick={onClearFilters}
                size="sm"
              />
            </SimpleGrid>
          </VStack>
        </CustomPopover>
      </Flex>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <Box
          mt={3}
          pt={3}
          borderTop="1px solid"
          borderColor={colors.border.subtle}
        >
          <VStack gap={2} align="start">
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Active filters:
            </Text>
            <Flex wrap="wrap" gap={2}>
              {getFilterSummary().map((filter, index) => (
                <Badge
                  key={index}
                  colorPalette={colors.schoolAccent.primary}
                  size="sm"
                  variant="subtle"
                  borderRadius="full"
                  px={2}
                >
                  {filter}
                </Badge>
              ))}
            </Flex>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default AuditTrailFilters;
